import { Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { displayToastError, displayToastSuccess } from '@/helpers';
import { Dropzone } from '@/modules/common/components/organisms/file/dropzone';
import { useImportEntitiesStore } from '@/modules/onboarding/common/import-entities-modal/state';
import {
  ImportFileState,
  mapToDropzoneStatus,
} from '@/modules/onboarding/common/import-entities-modal/types/common';
import { countCsvRows } from '@/utils/file/csv-utils';
import { logger } from '@/utils/logger/logger';

const TAG = 'UploadContent:';

export interface UploadContentStringResources {
  addedSuccessfully: string;
  title: string;
  failedImport: string;
  successImport: string;
  step1Description: string;
  step2Description: string;
  backToImport: string;
}

export interface EntityRelatedProps<TError> {
  res: UploadContentStringResources;
  templateLinkHref: string;
  templateLinkLabel: string;
  importEntitiesFromCsv: (file: File) => Promise<TError[]>;
  /** Called after a successful import. Use this to invalidate entity-specific query caches. */
  onSuccess?: (addedCount: number) => void;
}

interface UploadContentProps<TError> {
  entityRelatedProps: EntityRelatedProps<TError>;
  onClose: () => void;
}

export function UploadContent<TError>({
  onClose,
  entityRelatedProps: {
    templateLinkHref = '/templates/members-template.csv',
    templateLinkLabel = 'members-template.csv',
    importEntitiesFromCsv,
    onSuccess,
    res: {
      addedSuccessfully = 'authenticated.onboarding.team:importMembers.addedSuccessfully',
      title = 'authenticated.onboarding.team:importMembers.title',
      failedImport = 'authenticated.onboarding.team:importMembers.failedImport',
      successImport = 'authenticated.onboarding.team:importMembers.successImport',
      step1Description = 'authenticated.onboarding.team:importMembers.step1Description',
      step2Description = 'authenticated.onboarding.team:importMembers.step2Description',
    },
  },
}: UploadContentProps<TError>) {
  const { t } = useTranslation();

  const {
    file,
    parsedMembersCount,
    importErrors,
    importState,
    setFile,
    setParsedMembersCount,
    setImportState,
    setPreviewErrorOpen,
    setImportErrors,
  } = useImportEntitiesStore();

  const handleFileSelect = async (selectedFile: File | null) => {
    setFile(selectedFile);
    if (!selectedFile) {
      setImportState(ImportFileState.IDLE);
      return;
    }

    try {
      setImportState(ImportFileState.UPLOADING);
      const rowCount = await countCsvRows(selectedFile);
      setParsedMembersCount(rowCount);
      setImportState(ImportFileState.SUCCESS);
    } catch (error) {
      logger.error(TAG, 'Error counting CSV rows', error);
      setImportState(ImportFileState.IDLE);
    }
  };

  const downloadTemplate = () => {
    const link = document.createElement('a');
    link.href = templateLinkHref;
    link.download = templateLinkLabel;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleUploadFile = async () => {
    if (!file) return;

    try {
      logger.debug(TAG, 'Starting employee import from CSV', { fileName: file.name });

      setImportState(ImportFileState.UPLOADING);

      const importErrors = await importEntitiesFromCsv(file);

      if (importErrors.length > 0) {
        logger.warn(TAG, 'Import completed with errors', { errorCount: importErrors.length });
        setImportErrors(importErrors);
        setImportState(ImportFileState.ERROR);
        setPreviewErrorOpen(true);
      } else {
        logger.debug(TAG, 'Employee import completed successfully');
        displayToastSuccess(t(addedSuccessfully, { count: parsedMembersCount }));
        setImportState(ImportFileState.SUCCESS);
        onSuccess?.(parsedMembersCount);
        onClose();
      }
    } catch (error) {
      setImportState(ImportFileState.IDLE);
      logger.error(TAG, 'Error importing employees', error);
      displayToastError(
        error.message || t('authenticated.onboarding.common:importEntities.unknownImportError')
      );
    }
  };

  const renderConfirmButton = () => {
    return (
      <Button
        onClick={handleUploadFile}
        disabled={!file || importState === ImportFileState.UPLOADING}
      >
        {t('authenticated.onboarding.common:importEntities.uploadButton')}
      </Button>
    );
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>{t(title)}</DialogTitle>
      </DialogHeader>

      {/* Step 1 */}
      <div className="space-y-2">
        <div className="space-y-1">
          <p className="text-sm font-medium">
            {t('authenticated.onboarding.common:importEntities.step1Title')}
          </p>
          <p className="text-sm font-normal text-muted-foreground">{t(step1Description)}</p>
        </div>
        <Button variant="outline" className="gap-2" onClick={downloadTemplate}>
          {t('authenticated.onboarding.common:importEntities.downloadTemplate')}
          <Download />
        </Button>
      </div>

      {/* Step 2 */}
      <div className="space-y-2 overflow-hidden">
        <div className="space-y-1">
          <p className="text-sm font-medium">
            {t('authenticated.onboarding.common:importEntities.step2Title')}
          </p>
          <p className="text-sm font-normal text-muted-foreground">{t(step2Description)}</p>
        </div>
        <Dropzone
          accept=".csv"
          maxSize={5}
          file={file}
          setFile={handleFileSelect}
          status={mapToDropzoneStatus(importState)}
          errorMessage={t(failedImport, { count: importErrors.length })}
          errorActionLabel={t('authenticated.onboarding.common:importEntities.seeErrors')}
          onErrorAction={() => setPreviewErrorOpen(true)}
          successMessage={t(successImport, { count: parsedMembersCount })}
        />
      </div>

      {/* Footer buttons */}
      <div className="flex justify-end gap-2 pt-4">
        <Button variant="outline" onClick={onClose}>
          {t('common:cancel')}
        </Button>
        {renderConfirmButton()}
      </div>
    </>
  );
}
